import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { HiOutlineCheck } from "react-icons/hi";
import { schoolRegisterValidationSchema1 } from "./validation/school-register-form-validation";
import { Dispatch, SetStateAction } from "react";

interface IProps {
  passValue: number;
  setPassedValue: Dispatch<SetStateAction<number>>;
}

function SchoolRegisterFormStep1Component(props: IProps) {
  const { i18n, t } = useTranslation();

  const moreThanThreeCharsRegEx = /([a-zA-Z]|[0-9]|[-_.]){3,}/;
  const containAtLeastOneRegEx = /^[A-Za-z0-9_-]*[-_.][A-Za-z0-9_-]*$/;
  const mustNotStartWithNumberRegEx = /^[^0-9].*$/;
  const moreThanEightCharsRegEx = /([a-zA-Z]|[0-9]){8,}/;
  const containAtLeastOnePasswordRegEx = /[&?*%!$@]/;
  const containAtLeastOneLowerOneUpperRegEx = /^(?=.*[a-z])(?=.*[A-Z]).*$/;

  type InitialValuesType = {
    facilityFullName: string;
    email: string;
    userName: string;
    password: string;
    confirmPassword: string;
  };

  const initialValues: InitialValuesType = {
    facilityFullName: "",
    email: "",
    userName: "",
    password: "",
    confirmPassword: "",
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    isValid,
  } = useFormik({
    initialValues: initialValues,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: schoolRegisterValidationSchema1,
    onSubmit: async () => {
      handleSubmit();
    },
  });

  return (
    <form
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
      onSubmit={handleSubmit}
      className="inline-flex flex-col gap-4 items-center justify-center mobile:mt-20 tablet:mt-20"
    >
      <div className="flex flex-col gap-2">
        <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
          {t("registerForm.component.facilityFullName")}
        </label>
        <input
          value={values.facilityFullName}
          onChange={handleChange}
          onBlur={handleBlur}
          id="facilityFullName"
          type="text"
          className={`${
            touched.facilityFullName && errors.facilityFullName
              ? "border-secondary-50 focus:border-secondary-50"
              : "border-zinc400 focus:border-mint-500"
          } w-[425px] h-[61px] bg-transparent mobile:max-w-[345px] mobile:w-screen mobile:h-[61px] mobile px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
          placeholder={`${t("placeholder.facilityFullName.schoolNameExample")}`}
        ></input>
        {touched.facilityFullName && errors.facilityFullName && (
          <p className="text-base mobile:text-xs font-arMedium text-secondary-50">
            {errors.facilityFullName}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
          {t("registerForm.component.userName")}
        </label>
        <input
          value={values.userName}
          onChange={handleChange}
          onBlur={handleBlur}
          id="userName"
          type="text"
          className={`${
            touched.userName && errors.userName
              ? "border-secondary-50 focus:border-secondary-50"
              : "border-zinc400 focus:border-mint-500"
          } w-[425px] h-[61px] bg-transparent mobile:max-w-[345px] mobile:w-screen mobile:h-[61px] mobile px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
          placeholder="a-a12"
        ></input>
        {touched.userName && errors.userName && (
          <p className="text-base mobile:text-xs font-arMedium text-secondary-50">
            {errors.userName}
          </p>
        )}
        <p
          className={`${
            moreThanThreeCharsRegEx.test(values.userName)
              ? "text-green500"
              : "text-zinc400"
          } flex flex-row items-center mt-1 gap-1 text-sm font-ar`}
        >
          <HiOutlineCheck className="text-2xl -mt-1.5" />
          {t("form.error.userName.checkOne")}
        </p>
        <p
          className={`${
            containAtLeastOneRegEx.test(values.userName)
              ? "text-green500"
              : "text-zinc400"
          } flex flex-row items-center gap-1 text-sm font-ar`}
        >
          <HiOutlineCheck className="text-2xl -mt-1.5" />
          {t("form.error.userName.checkTwo")}
        </p>
        <p
          className={`${
            mustNotStartWithNumberRegEx.test(values.userName)
              ? "text-green500"
              : "text-zinc400"
          } flex flex-row items-center gap-1 text-sm font-ar`}
        >
          <HiOutlineCheck className="text-2xl -mt-1.5" />
          {t("form.error.userName.checkThree")}
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
          {t("registerForm.component.email")}
        </label>
        <input
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          id="email"
          type="text"
          className={`${
            touched.email && errors.email
              ? "border-secondary-50 focus:border-secondary-50"
              : "border-zinc400 focus:border-mint-500"
          } w-[425px] h-[61px] bg-transparent mobile:max-w-[345px] mobile:w-screen mobile:h-[61px] mobile px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
          placeholder="a@example.sa"
        ></input>
        {touched.email && errors.email && (
          <p className="text-base mobile:text-xs font-arMedium text-secondary-50">
            {errors.email}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
          {t("registerForm.component.password")}
        </label>
        <input
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          id="password"
          type="password"
          className={`${
            touched.password && errors.password
              ? "border-secondary-50 focus:border-secondary-50"
              : "border-zinc400 focus:border-mint-500"
          } w-[425px] h-[61px] bg-transparent mobile:max-w-[345px] mobile:w-screen mobile:h-[61px] mobile px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
          placeholder="************"
        ></input>
        {touched.password && errors.password && (
          <p className="text-base mobile:text-xs font-arMedium text-secondary-50">
            {errors.password}
          </p>
        )}
        <p
          className={`${
            containAtLeastOnePasswordRegEx.test(values.password)
              ? "text-green500"
              : "text-zinc400"
          } flex flex-row items-center mt-1 gap-1 text-sm font-ar`}
        >
          <HiOutlineCheck className="text-2xl -mt-1.5" />
          {t("form.error.password.checkOne")}
        </p>
        <p
          className={`${
            containAtLeastOneLowerOneUpperRegEx.test(values.password)
              ? "text-green500"
              : "text-zinc400"
          } flex flex-row items-center gap-1 text-sm font-ar`}
        >
          <HiOutlineCheck className="text-2xl -mt-1.5" />
          {t("form.error.password.checkTwo")}
        </p>
        <p
          className={`${
            moreThanEightCharsRegEx.test(values.password)
              ? "text-green500"
              : "text-zinc400"
          } flex flex-row items-center gap-1 text-sm font-ar`}
        >
          <HiOutlineCheck className="text-2xl -mt-1.5" />
          {t("form.error.password.checkThree")}
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
          {t("registerForm.component.confirmPassword")}
        </label>
        <input
          value={values.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          id="confirmPassword"
          type="password"
          className={`${
            touched.confirmPassword && errors.confirmPassword
              ? "border-secondary-50 focus:border-secondary-50"
              : "border-zinc400 focus:border-mint-500"
          } w-[425px] h-[61px] bg-transparent mobile:max-w-[345px] mobile:w-screen mobile:h-[61px] mobile px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
          placeholder="************"
        ></input>
        {touched.confirmPassword && errors.confirmPassword && (
          <p className="text-base mobile:text-xs font-arMedium text-secondary-50">
            {errors.confirmPassword}
          </p>
        )}
      </div>
      <button
        onClick={() => {
          touched.facilityFullName &&
            touched.email &&
            touched.userName &&
            touched.password &&
            touched.confirmPassword &&
            props.setPassedValue(1);
        }}
        disabled={!isValid}
        type="submit"
        className="w-[345px] h-[55px] mobile:max-w-[345px] mobile:w-screen mobile:h-[61px] px-4 py-2 bg-mint-500 rounded-xl justify-center items-center gap-2 inline-flex text-white text-xl font-arMedium leading-[30px] cursor-pointer"
      >
        {t("forms.component.next")}
      </button>
    </form>
  );
}

export default SchoolRegisterFormStep1Component;
